import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lightning } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body grid lg:grid-cols-2">
      {/* Left side: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <Lightning weight="fill" size={22} className="text-[#FF2A2A]" />
            <span className="font-heading font-black text-xl tracking-tight">
              RECRUITFLOW
            </span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Welcome back
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl mb-10 leading-[1.05]">
            Sign in to your<br />deal board.
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-950 underline underline-offset-4"
                >
                  Forgot?
                </Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-4 py-3 rounded-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-zinc-950 text-white py-3.5 rounded-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            New here?{" "}
            <Link to="/register" className="text-zinc-950 underline underline-offset-4 hover:no-underline" data-testid="goto-register">
              Create an account
            </Link>
          </div>
        </div>
      </div>

      {/* Right side: visual */}
      <div className="hidden lg:flex relative bg-zinc-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 mix-blend-screen"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/7135007/pexels-photo-7135007.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 p-16 flex flex-col justify-between w-full">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            Recruitment Deal Tracker
          </div>
          <div>
            <div className="font-heading font-black text-5xl tracking-tight leading-[1.05] mb-6">
              Every candidate.<br />
              Every deal.<br />
              Every fee.
            </div>
            <div className="text-zinc-300 text-base max-w-md leading-relaxed">
              Track candidates through interview slots, watch fees mature on the board, and close more placements — all in one focused workspace built for recruiters.
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            v1.0 — Built for recruiters
          </div>
        </div>
      </div>
    </div>
  );
}
